import type { WebGLRenderer, Scene, PerspectiveCamera } from 'three';
import type { AutomationApi } from './types';
import type { ControlState, Pose, ControlMode } from '../controls/types';
import type { WorldStats } from '../world/types';
import type { PostPipeline } from '../render/types';
import { renderedFrameCount, simulatedSeconds } from './frameLoop';
import type { NetworkDiagnostics } from '../multiplayer/client';
import type { VisitorMovementDiagnostics } from '../multiplayer/visitorLocomotion';
import type { Townsperson } from '../communication/types';
import { rooftopDiagnostics } from './rooftopDiagnostics';
import { animalDiagnostics } from './animalDiagnostics';
import { harbourDiagnostics } from './harbourDiagnostics';

export function createAutomationApi(
  renderer: WebGLRenderer,
  scene: Scene,
  camera: PerspectiveCamera,
  pipeline: PostPipeline,
  getControlState: () => ControlState,
  getWorldStats: () => WorldStats,
  buildMs: number,
  getRenderHeight: () => number,
  getNetwork: () => NetworkDiagnostics | null = () => null,
  getVisitors: () => readonly VisitorMovementDiagnostics[] = () => [],
  getTownspeople: () => readonly Townsperson[] = () => [],
  getCommunications: AutomationApi['communications'] = () => ({
    selected: null,
    following: null,
    players: [],
    bubbles: [],
  }),
  getDiscoveries: AutomationApi['discoveries'] = () => ({
    total: 0,
    found: [],
    card: null,
    open: false,
    keeping: false,
    failing: false,
  }),
  getPainterAmount: () => number | null = () => null
): AutomationApi {
  return {
    ready: true,
    multiplayer: getNetwork,
    visitors: getVisitors,
    townspeople: getTownspeople,
    communications: getCommunications,
    discoveries: getDiscoveries,
    rooftops: () => rooftopDiagnostics(scene),
    harbour: () => harbourDiagnostics(scene),
    animals: () => animalDiagnostics(scene),
    buildMs,

    painting() {
      return {
        projectionAmount: getPainterAmount(),
        handoffOpacity: pipeline.painterHandoff?.opacity ?? 0,
        fovYDeg: camera.fov,
      };
    },

    mode(): ControlMode {
      return getControlState().mode;
    },

    pose(): Pose {
      return getControlState().pose;
    },

    renderInfo(): { readonly calls: number; readonly triangles: number } {
      const info = renderer.info;
      return {
        calls: info.render.calls,
        triangles: info.render.triangles,
      };
    },

    worldStats(): WorldStats {
      return getWorldStats();
    },

    capturePng(): string {
      renderer.info.reset();
      pipeline.render(scene, camera);
      return renderer.domElement.toDataURL('image/png');
    },

    frames(): number {
      return renderedFrameCount();
    },

    simulatedSeconds(): number {
      return simulatedSeconds();
    },

    renderHeight(): number {
      return getRenderHeight();
    },
  };
}
